import { BookOpenText } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import type { SubjectCard } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { subjectKindMeta } from "@/lib/subjectKinds";
import { WorldViewActivityLine } from "@/components/WorldViewActivityLine";

/**
 * UI-10 — the world view index. The first screen where the world view is visible at all.
 *
 * Called **World view**, never "wiki". The 2026-08-23 vocabulary decision is strict about
 * this: the destination is the world view, and "wiki" appears only where the artifact
 * itself does — the file path on the subject page, and the git line in its footer.
 *
 * What it renders comes from the belief store, not from the git working copy. The
 * repository is a publish target (WORLD-5) and the read path deliberately has no way to
 * open it, which is what keeps that true rather than remembered.
 */
export function WorldViewPage() {
  const { worldView, worldViewLoading, worldViewProblem, refreshWorldView, worldViewActivity } = useApp();
  const [searchParams] = useSearchParams();

  // Re-read on arrival rather than poll. Synthesis finishes on whichever machine the
  // `Reasoner` is placed on and nothing tells this browser when — so the moment somebody
  // comes to look is the moment worth asking, and a timer would be a request every few
  // seconds for an answer that changes a few times a week.
  useEffect(() => refreshWorldView(), [refreshWorldView]);

  const kindFilter = searchParams.get("kind");

  const shown = useMemo(() => {
    const subjects = worldView?.subjects ?? [];
    return subjects.filter((subject) => !kindFilter || kindFilter === "all" || subject.kind === kindFilter);
  }, [worldView, kindFilter]);

  const total = worldView?.subjects.length ?? 0;

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-col gap-[var(--space-2)]">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          World view
        </h1>
        <p className="max-w-[72ch] text-[length:var(--text-md)] text-[var(--text-secondary)]">
          Everything known about a subject, on one page, written from your recordings. Every line points
          back to where it was said.
        </p>

        {!worldViewLoading && total > 0 && (
          <p className="flex flex-wrap items-center gap-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {total} {total === 1 ? "subject" : "subjects"} ·{" "}
            {worldViewActivity ? (
              <WorldViewActivityLine activity={worldViewActivity} />
            ) : worldView?.lastRewrittenAt ? (
              `last rewritten ${formatRelative(worldView.lastRewrittenAt)}`
            ) : (
              "not rewritten yet"
            )}
          </p>
        )}
      </header>

      {worldViewProblem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {worldViewProblem}
        </p>
      )}

      {worldViewLoading ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : shown.length === 0 ? (
        <Empty filtered={total > 0} />
      ) : (
        // Two columns, per the handoff. Measured rather than fixed, so a narrow window
        // collapses to one instead of squeezing a summary into forty characters — but the
        // minimum is wide enough that the content frame's 1120 px never fits a third.
        <div className="grid grid-cols-[repeat(auto-fill,minmax(420px,1fr))] gap-[var(--space-4)]">
          {shown.map((subject) => (
            <Card key={subject.id} subject={subject} />
          ))}
        </div>
      )}
    </div>
  );
}

function Card({ subject }: { subject: SubjectCard }) {
  const meta = subjectKindMeta(subject.kind);
  const Icon = meta.icon;

  return (
    <Link
      to={`/world-view/${subject.slug}`}
      className="flex flex-col gap-2 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] transition hover:-translate-y-0.5 hover:border-[var(--border-default)] hover:shadow-[var(--shadow-md)]"
    >
      <span className="flex items-center gap-1.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        <Icon size={12} strokeWidth={2.5} />
        {meta.label}
      </span>

      <span className="text-[length:var(--text-lg)] font-bold text-[var(--text-primary)]">{subject.title}</span>

      {subject.summary && (
        <span className="line-clamp-3 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          {subject.summary}
        </span>
      )}

      <span className="mt-auto pt-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">
        {subject.beliefCount} {subject.beliefCount === 1 ? "belief" : "beliefs"} · updated{" "}
        {formatRelative(subject.updatedAt)}
      </span>
    </Link>
  );
}

/**
 * Two different nothings, said differently. A world view with no subjects at all is waiting
 * on the pipeline and says what starts it; one that is merely filtered down to nothing is a
 * filter with no matches, and saying "approve some insights" there would be wrong.
 */
function Empty({ filtered }: { filtered: boolean }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] p-[var(--space-8)] text-center">
      <BookOpenText size={22} strokeWidth={1.75} className="text-[var(--text-muted)]" />

      {filtered ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          No subjects of that kind yet.{" "}
          <Link to="/world-view" className="underline underline-offset-2 hover:text-[var(--text-primary)]">
            Show all pages
          </Link>
          .
        </p>
      ) : (
        <p className="max-w-[52ch] text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          Nothing is held yet. Subjects appear when you approve insights in the{" "}
          <Link to="/review" className="underline underline-offset-2 hover:text-[var(--text-primary)]">
            review queue
          </Link>
          — approving is what asks the system to work out what they mean.
        </p>
      )}
    </div>
  );
}
